import React, { createContext, useContext, useEffect, useReducer } from "react";
import locale from "locale";
import { getColumnColor } from '../models';
import { Table, FormControl, FormGroup, ControlLabel } from "react-bootstrap";
import { CUSTOM_VALUE, MAX_EXCEL_PREVIEW_ROWS } from '../constants';

import './registration-fields-table.scss';
import RenderField from './RenderField';

export const SheetsContext = createContext(null);

const RegistrationFieldsTable = ({ state, event, roles, participationTypes, sheets, fields, children, updateState }) => {    
    useEffect(() => {
        const current = sheets[state.sheetIndex] ?? [];
        
        updateState({ sheet: current });

        current.forEach((row) =>
            Object.keys(row).forEach((index) =>
                updateState({
                    columnCount: Math.max(
                        state.columnCount,
                        parseInt(index) + 1
                    ),
                })
            )
        );

        if (
            !Object.keys(state.selectedColumns)?.length
            || Object.keys(state.selectedColumns)?.length !== Object.keys(fields)?.length
        ) {
            let selectedColumns = {};
            let customValues = {};
            
            fields.forEach((field) => {
                selectedColumns[field.name] = null;
                customValues[field.name] = '';
            });
    
            updateState({selectedColumns, customValues});
        }

    }, [state.sheetIndex, fields]);

    function getColumns() {
        return new Array(state.columnCount)
            .fill(true)
            .map((_, index) => baseConvert(index));
    }

    function baseConvert(number, letters) {
        letters =
            letters ||
            new Array(26)
                .fill(true)
                .map((_, index) => String.fromCharCode(65 + index));

        if (number >= 0 && number < letters.length) {
            return letters[number];
        }

        return (
            baseConvert(parseInt(number / letters.length) - 1) +
            baseConvert(number % letters.length)
        );
    }

    function handleSelectColumn(e, field) {
        const selectedColumns = Object.keys(state.selectedColumns).reduce((acc, key) => {
            if (key === field) acc[field] = !!e.target.value ? +e.target.value : null;

            return acc;
        }, state.selectedColumns);

        updateState({selectedColumns, customValues: { ...state.customValues, [field]: ''}});
    }

    const context = {state, getColumns, fields, updateState};

    return (
        <>
            <Table>
                <tbody>
                    <tr>
                        <td>
                            <FormGroup>
                                <ControlLabel>{locale("reminders.sheet")}</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={state.sheetIndex}
                                    onChange={({ target }) =>
                                        updateState({
                                            sheetIndex: parseInt(target.value),
                                        })
                                    }
                                >
                                    {sheets?.length && sheets.map((_, index) => (
                                        <option key={index} value={index}>
                                            {index + 1}
                                        </option>
                                    ))}
                                </FormControl>
                            </FormGroup>
                        </td>
                        <td>
                            <FormGroup>
                                <ControlLabel>{locale('importExcel.modal.table.rowNumber')}</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    name="rowSelect"
                                    value={state.selectedRow}
                                    onChange={({ target }) =>
                                        updateState({
                                            selectedRow: parseInt(target.value),
                                        })
                                    }
                                >
                                    {
                                        new Array(MAX_EXCEL_PREVIEW_ROWS)
                                            .fill(true)
                                            .map((_, index) => (
                                                <option key={index} value={index}>
                                                    {index + 1}
                                                </option>
                                            ))
                                    }
                                </FormControl>
                            </FormGroup>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <FormGroup>
                                <ControlLabel>{locale("role")}</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={state.role || ''}
                                    onChange={({ target }) =>
                                        updateState({
                                            role: target.value,
                                        })
                                    }
                                >
                                    <option value="" disabled>Выберите значение</option>
                                    {roles?.length && roles.map(({label, value}) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </FormControl>
                            </FormGroup>
                        </td>
                        <td>
                            <FormGroup>
                                <ControlLabel>{locale('participationType')}</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    name="rowSelect"
                                    value={state.participationType || ''}
                                    disabled={event.participationType !== 'hybrid'}
                                    onChange={({ target }) =>
                                        updateState({
                                            participationType: target.value,
                                        })
                                    }
                                >
                                    <option value="" disabled>Выберите значение</option>
                                    {participationTypes?.length && participationTypes.map(({label, value}) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </FormControl>
                            </FormGroup>
                        </td>
                    </tr>
                    {
                        fields?.length
                        && fields
                            .map((field, i) => (
                                <tr key={`${field}-${i}`}>
                                    <td>
                                        <div className="reg-field-colored-name">
                                            <span style={{backgroundColor: getColumnColor(i)}} />
                                            <p>
                                                {field[`label_${locale.currentLocaleName}`]}
                                            </p>
                                        </div>
                                    </td>
                                    <td>
                                        <FormGroup>
                                            <ControlLabel>Значение поля</ControlLabel>
                                            <FormControl
                                                componentClass="select"
                                                name="colorSelect"
                                                value={state.selectedColumns?.[field.name] ?? ''}
                                                onChange={(e) => handleSelectColumn(e, field.name)}
                                                style={{width: '100%'}}
                                            >
                                                <option value="">Пустое значение</option>
                                                <option value={CUSTOM_VALUE}>Свой вариант</option>
                                                {
                                                    getColumns()
                                                    .map((name, index) => (
                                                        <option
                                                            key={index}
                                                            value={index}
                                                            disabled={Object.values(state.selectedColumns).includes(index)}
                                                        >
                                                            {name}
                                                        </option>
                                                    ))
                                                }
                                            </FormControl>
                                        </FormGroup>
                                        { state.selectedColumns?.[field.name] === CUSTOM_VALUE &&
                                            <RenderField
                                                field={field}
                                                value={state.customValues[field.name] || ''}
                                                label="Свое значение"
                                                placeholder="Укажите свой вариант"
                                                onChange={(value) => updateState({customValues: {...state.customValues, [field.name]: value}})}
                                            />
                                        }
                                    </td>
                                </tr>
                            ))
                    }
                </tbody>
            </Table>
            <SheetsContext.Provider value={context}>
                {children}
            </SheetsContext.Provider>
        </>
    );
};

export default RegistrationFieldsTable;

export const useSheetsContext = () => useContext(SheetsContext);
